// src/services/enhancedUserService.js - Fixed with Permission Guards
import { db, auth } from '../firebase';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  increment,
  arrayUnion,
  writeBatch
} from 'firebase/firestore';

/** Recursively strip undefined so Firestore addDoc never throws. Leaves non-plain objects (e.g. serverTimestamp()) intact. */
function stripUndefined(obj) {
  if (obj === undefined) return undefined;
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined).filter((v) => v !== undefined);
  if (Object.getPrototypeOf(obj) !== Object.prototype) return obj;
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, stripUndefined(v)])
  );
}

/**
 * Enhanced User Service - with proper error handling for permissions
 */
class EnhancedUserService {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  /**
   * Enhanced user profile creation - integrates with your AuthPage
   */
  async createEnhancedUserProfile(user, signupData = {}) {
    try {
      console.log('Creating enhanced user profile for:', user.uid);
      const userRef = doc(db, 'users', user.uid);
      const browserLang = navigator.language.split('-')[0]; // 'en-US' -> 'en'
    const supportedLanguages = ['en', 'de', 'es', 'fr', 'tr', 'ru'];
    const defaultLanguage = supportedLanguages.includes(browserLang) ? browserLang : 'en';

      const enhancedProfile = {
        // Basic info
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || signupData.displayName || '',
        firstName: signupData.firstName || '',
        lastName: signupData.lastName || '',
        emailVerified: user.emailVerified,
        // Avatar/Photo URLs
        photoURL: user.photoURL || null,
        avatarUrl: user.photoURL || null, // Redundancy for compatibility

        // Existing userService compatibility
        userId: user.uid,
        totalAnswered: 0,
        correctAnswers: 0,
        sectionScores: {},
        completedWeeks: [],
        studyStreak: 0,
        lastStudyDate: null,
        overallProgress: 0,
        sectionProgress: {},


        // Enhanced tracking
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        profileCompleted: false,

        // Study preferences
        preferences: {
          language: defaultLanguage,
          notifications: true,
          emailUpdates: true,
          studyReminders: true,
          targetLanguage: 'German',
          currentLevel: 'A1',
          dailyGoal: 30,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },

        // Statistics
        stats: {
          lessonsCompleted: 0,
          lessonsStarted: 0,
          examsCompleted: 0,
          examsStarted: 0,
          examsPassed: 0,
          examsFailed: 0,
          averageExamScore: 0,
          bestExamScore: 0,
          practiceSessionsCompleted: 0,
          practiceTimeMinutes: 0,
          vocabularyWordsLearned: 0,
          vocabularyWordsReviewed: 0,
          vocabularyMastered: 0,
          grammarTopicsCompleted: 0,
          grammarExercisesCompleted: 0,
          listeningExercises: 0,
          readingExercises: 0,
          writingExercises: 0,
          speakingExercises: 0,
          totalStudyTimeMinutes: 0,
          totalCorrectAnswers: 0,
          totalQuestionsAttempted: 0,
          overallAccuracy: 0,
          currentStreak: 0,
          longestStreak: 0,
          achievementsUnlocked: 0,
          totalPoints: 0
        },

        // Study plan integration
        studyPlan: {
          currentPlan: null,
          planStartDate: null,
          planEndDate: null,
          intensity: 'moderate',
          focusAreas: [],
          planProgress: 0
        },

        // Achievements system
        achievements: {
          unlocked: [],
          inProgress: {},
          totalPoints: 0
        },

        // Subscription status
        subscription: {
          type: 'free',
          startDate: null,
          endDate: null,
          features: ['basic_lessons', 'daily_practice']
        },
        
        // Subscription fields for Stripe integration
        subscriptionStatus: 'free',
        currentPlan: 'free',
        subscriptionStartDate: null,
        subscriptionEndDate: null,
        
        // Exam selection (from signup)
        selectedExamProvider: signupData.selectedExamProvider || null,
        selectedExamLevel: signupData.selectedExamLevel || null
      };

      await setDoc(userRef, enhancedProfile);
      console.log('Enhanced user profile created successfully');
      return { success: true, data: enhancedProfile };
    } catch (error) {
      console.error('Error creating enhanced user profile:', error);
      return { success: false, error: error.message };
    }
  }
  /**
   * Update user avatar URL in Firestore
   */
  async updateUserAvatar(userId, photoURL) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        photoURL: photoURL,
        avatarUrl: photoURL, // Redundancy for backward compatibility
        updatedAt: serverTimestamp()
      });
      console.log('User avatar updated in Firestore');
      return { success: true };
    } catch (error) {
      console.error('Error updating user avatar:', error);
      return { success: false, error: error.message };
    }
  }
  /**
   * Get user profile with avatar
   */
  async getUserProfile(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Ensure photoURL is returned
        return {
          ...userData,
          photoURL: userData.photoURL || userData.avatarUrl || null
        };
      }

      // Create default profile if doesn't exist
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === userId) {
        const newProfile = await this.createEnhancedUserProfile(currentUser);
        return newProfile;
      }

      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  /**
   * Get complete user data - WITH PERMISSION ERROR HANDLING
   */
  async getCompleteUserData(userId = null) {
    const targetUserId = userId || auth.currentUser?.uid;
    if (!targetUserId) {
      console.warn('No user ID provided for getCompleteUserData');
      return null;
    }

    try {
      console.log('Getting complete user data for:', targetUserId);

      // Get main user profile
      const userRef = doc(db, 'users', targetUserId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        console.log('User document does not exist');
        return null;
      }

      const userProfileData = userDoc.data(); // This is the data we need to nest

      // Containers for sub-collection data
      let recentVocabulary = [];
      let recentAchievements = [];
      let recentSessions = [];
      let recentExams = [];

      // Try to get vocabulary data with permission handling
      try {
        const vocabQuery = query(
          collection(db, 'users', targetUserId, 'vocabulary'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const vocabSnapshot = await getDocs(vocabQuery);
        recentVocabulary = vocabSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (vocabError) {
        if (this.isDevelopment) {
          console.warn('Could not fetch vocabulary:', vocabError.message);
        }
      }

      // Try to get achievements data with permission handling
      try {
        const achievementsQuery = query(
          collection(db, 'users', targetUserId, 'achievements'),
          orderBy('unlockedAt', 'desc')
        );
        const achievementsSnapshot = await getDocs(achievementsQuery);
        recentAchievements = achievementsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (achievementError) {
        if (this.isDevelopment) {
          console.warn('Could not fetch achievements:', achievementError.message);
        }
      }

      // Try to get study sessions with permission handling
      try {
        const sessionsQuery = query(
          collection(db, 'users', targetUserId, 'studySessions'),
          orderBy('startTime', 'desc'),
          limit(20)
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);
        recentSessions = sessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (sessionsError) {
        if (this.isDevelopment) {
          console.warn('Could not fetch study sessions:', sessionsError.message);
        }
      }

      // Try to get exam results with permission handling
      try {
        const resultsQuery = query(
          collection(db, 'users', targetUserId, 'examResults'),
          orderBy('completedAt', 'desc'),
          limit(10)
        );
        const resultsSnapshot = await getDocs(resultsQuery);
        recentExams = resultsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (resultsError) {
        if (this.isDevelopment) {
          console.warn('Could not fetch exam results:', resultsError.message);
        }
      }

      console.log('Complete user data retrieved successfully');

      // Derive completed lesson IDs from study sessions so study plan can show finished state
      const completedLessons = [...new Set(
        recentSessions
          .filter(s => s.type === 'lesson' && s.completed && s.activityId)
          .map(s => s.activityId)
      )];

      return {
        success: true,
        data: {
          profile: {
            ...userProfileData,
            photoURL: userProfileData.photoURL || userProfileData.avatarUrl || null
          },
          recentSessions,
          recentExams,
          recentVocabulary,
          recentAchievements,
          completedLessons
        }
      };

    } catch (error) {
      console.error('Error getting complete user data:', error);
      return null;
    }
  }
  /**
   * Track vocabulary words learned or reviewed
   */
  async trackVocabulary(userId, words, status = 'learned') {
    if (!userId || !words || words.length === 0) {
      return { success: false, error: 'Missing required parameters' };
    }

    try {
      const vocabRef = collection(db, 'users', userId, 'vocabulary');
      const userRef = doc(db, 'users', userId);
      const batch = writeBatch(db);

      words.forEach(word => {
        const wordRef = doc(vocabRef, word.german); // Use German word as ID
        batch.set(wordRef, {
          ...word,
          status: status,
          lastReviewed: serverTimestamp(),
          createdAt: serverTimestamp(),
        }, { merge: true });
      });

      await batch.commit();

      // Update aggregate stats
      const statsUpdate = {};
      if (status === 'learned') {
        statsUpdate['stats.vocabularyWordsLearned'] = increment(words.length);
      } else if (status === 'reviewed') {
        statsUpdate['stats.vocabularyWordsReviewed'] = increment(words.length);
      }
      await updateDoc(userRef, statsUpdate);

      return { success: true };
    } catch (error) {
      console.error('Error tracking vocabulary:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all vocabulary for a user
   */
  async getVocabulary(userId) {
    if (!userId) return [];
    try {
      const vocabQuery = query(
        collection(db, 'users', userId, 'vocabulary'),
        orderBy('createdAt', 'desc')
      );
      const vocabSnapshot = await getDocs(vocabQuery);
      return vocabSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting vocabulary:', error);
      return [];
    }
  }

  /**
   * Unlock an achievement for a user
   */
  async unlockAchievement(userId, achievementId, achievementData) {
    if (!userId || !achievementId) return { success: false, error: 'Missing parameters' };

    try {
      const userRef = doc(db, 'users', userId);
      const achievementRef = doc(db, 'users', userId, 'achievements', achievementId);

      // Use a transaction to ensure data consistency
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) {
          throw "User document does not exist!";
        }

        const achievements = userDoc.data().achievements || { unlocked: [], totalPoints: 0 };
        if (achievements.unlocked.includes(achievementId)) {
          console.log('Achievement already unlocked.');
          return; // Exit transaction
        }

        // Set the new achievement in its subcollection
        transaction.set(achievementRef, {
          ...achievementData,
          id: achievementId,
          unlockedAt: serverTimestamp(),
        });

        // Update the user's main document
        transaction.update(userRef, {
          'achievements.unlocked': arrayUnion(achievementId),
          'achievements.totalPoints': increment(achievementData.points || 0),
          'stats.achievementsUnlocked': increment(1),
          'stats.totalPoints': increment(achievementData.points || 0),
          updatedAt: serverTimestamp(),
        });
      });

      console.log(`Achievement unlocked: ${achievementId}`);
      return { success: true };
    } catch (error) {
      console.error('Error unlocking achievement:', error);
      return { success: false, error: error.message };
    }
  }
  async updateStudyStreak(userId) {
  if (!userId) {
    console.error("No userId provided for streak update");
    return null;
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      console.error("Cannot update streak, user doc does not exist");
      return null;
    }

    const userData = userDoc.data();
    const stats = userData.stats || {};

    // Get current streak from single source
    const currentStreak = stats.currentStreak || 0;

    // Get last study date and last streak update date
    const lastStudyTimestamp = userData.lastStudyDate;
    const lastStreakUpdate = userData.lastStreakUpdate; // New field to track when streak was last incremented

    // Get today's date at midnight (local timezone)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    today.setHours(0, 0, 0, 0);

    // Get yesterday's date at midnight
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let lastStudyDate = null;
    if (lastStudyTimestamp) {
      const tempDate = lastStudyTimestamp.toDate ? lastStudyTimestamp.toDate() : new Date(lastStudyTimestamp);
      lastStudyDate = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
      lastStudyDate.setHours(0, 0, 0, 0);
    }

    let lastStreakDate = null;
    if (lastStreakUpdate) {
      const tempDate = lastStreakUpdate.toDate ? lastStreakUpdate.toDate() : new Date(lastStreakUpdate);
      lastStreakDate = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
      lastStreakDate.setHours(0, 0, 0, 0);
    }

    // Determine if we should update the streak
    let newStreak = currentStreak;
    let shouldUpdateStreak = false;

    // Debug logging
    console.log('🔍 Streak check:', {
      currentStreak,
      lastStudyDate: lastStudyDate ? lastStudyDate.toLocaleDateString() : 'none',
      lastStreakDate: lastStreakDate ? lastStreakDate.toLocaleDateString() : 'none',
      today: today.toLocaleDateString(),
      yesterday: yesterday.toLocaleDateString()
    });

    if (!lastStudyDate) {
  // First time studying - start streak at 1
  newStreak = 1;
  shouldUpdateStreak = true;
  console.log('First study session - starting streak at 1');
} else if (!lastStreakDate) {
  // We have study history but no streak update tracking yet
  // Check if we should continue or reset based on last study
  const daysSinceLastStudy = Math.floor((today - lastStudyDate) / (1000 * 60 * 60 * 24));
  if (daysSinceLastStudy === 0) {
    // Studied today already - but still update lastStudyDate to mark login as activity
    await updateDoc(userRef, {
      lastStudyDate: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    console.log('Already studied today, keeping streak:', currentStreak);
    return currentStreak;
  } else if (daysSinceLastStudy === 1) {
    // Studied yesterday - continue streak (login counts as activity)
    newStreak = currentStreak + 1;
    shouldUpdateStreak = true;
    console.log('Continuing streak (no streak tracking yet, login counts as activity):', newStreak);
  } else {
    // Missed days - reset
    newStreak = 1;
    shouldUpdateStreak = true;
    console.log('Resetting streak (missed days)');
  }
}else if (lastStreakDate.getTime() === today.getTime()) {
      // Already incremented streak today, but still update lastStudyDate to mark login as activity
      // This ensures that logging in counts as an activity for streak purposes
      const lastStudyDateToday = lastStudyDate && lastStudyDate.getTime() === today.getTime();
      if (!lastStudyDateToday) {
        // Update lastStudyDate to today to mark login as activity
        await updateDoc(userRef, {
          lastStudyDate: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        console.log('Streak already updated today, but updated lastStudyDate for login activity');
      } else {
        console.log('Streak already updated today, keeping:', currentStreak);
      }
      return currentStreak;
    } else {
      // Check days since last streak update (primary check for continuing streaks)
      const daysSinceLastStreak = Math.floor((today - lastStreakDate) / (1000 * 60 * 60 * 24));
      
      if (daysSinceLastStreak === 1) {
        // Streak was updated yesterday - continue streak (logging in counts as activity)
        newStreak = currentStreak + 1;
        shouldUpdateStreak = true;
        console.log('Continuing streak from yesterday (login counts as activity):', newStreak);
      } else if (daysSinceLastStreak === 0) {
        // Same day - shouldn't reach here due to earlier check, but handle it
        console.log('Same day as last streak update, keeping:', currentStreak);
        // Still update lastStudyDate to mark login as activity
        await updateDoc(userRef, {
          lastStudyDate: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        return currentStreak;
      } else {
        // Missed one or more days - reset streak
        const daysMissed = daysSinceLastStreak;
        newStreak = 1;
        shouldUpdateStreak = true;
        console.log(`Missed ${daysMissed} days - resetting streak to 1`);
      }
    }

    if (shouldUpdateStreak) {
      // Update longest streak
      const longestStreak = Math.max(stats.longestStreak || 0, newStreak);

      // Update object
      const updates = {
        'stats.currentStreak': newStreak,
        'stats.longestStreak': longestStreak,
        'studyStreak': newStreak, // Keep for backward compatibility
        lastStudyDate: serverTimestamp(),
        lastStreakUpdate: serverTimestamp(), // Track when streak was updated
        updatedAt: serverTimestamp()
      };

      await updateDoc(userRef, updates);

      console.log(`✅ Streak updated: ${currentStreak} → ${newStreak} (Date: ${today.toLocaleDateString()})`);
      return newStreak;
    }

    // Update only lastStudyDate without changing streak
    await updateDoc(userRef, {
      lastStudyDate: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    console.log('Study date updated, streak remains:', currentStreak);
    return currentStreak;

  } catch (error) {
    console.error("❌ Error updating study streak:", error);
    return null;
  }
}
// ── Practice writing history ─────────────────────────────────────────────────
async savePracticeWritingHistory(userId, historyData) {
  if (!userId || !historyData) return null;
  try {
    const ref = collection(db, 'users', userId, 'practiceWritingHistory');
    const docData = stripUndefined({
      ...historyData,
      savedAt: serverTimestamp(),
    });
    // Key the entry by submissionId so the pending doc written at submission time
    // and the corrected doc written when feedback arrives are the same document.
    if (historyData.submissionId) {
      const docRef = doc(ref, historyData.submissionId);
      await setDoc(docRef, docData, { merge: true });
      return docRef.id;
    }
    const docRef = await addDoc(ref, docData);
    return docRef.id;
  } catch (err) {
    console.error('savePracticeWritingHistory error:', err);
    return null;
  }
}

async loadPracticeWritingHistory(userId, provider, level) {
  if (!userId) return [];
  try {
    const ref = collection(db, 'users', userId, 'practiceWritingHistory');
    const q = query(
      ref,
      where('provider', '==', provider),
      where('level', '==', level),
      orderBy('savedAt', 'desc'),
      limit(30)
    );
    const snap = await getDocs(q);
    const entries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // Self-heal: corrections that finished after the user left the page were never
    // written back by the client listener — pull them from writingSubmissions now.
    return await Promise.all(entries.map(async (entry) => {
      if (entry.feedback || !entry.submissionId || entry.status === 'failed') return entry;
      try {
        const submissionSnap = await getDoc(doc(db, 'writingSubmissions', entry.submissionId));
        if (!submissionSnap.exists()) return entry;
        const submission = submissionSnap.data();
        if (submission.status === 'corrected') {
          setDoc(doc(ref, entry.id), stripUndefined({ feedback: submission, status: 'corrected' }), { merge: true })
            .catch((err) => console.warn('Persisting healed writing history entry failed:', err));
          return { ...entry, feedback: submission, status: 'corrected' };
        }
        if (submission.status === 'failed' || String(submission.status || '').startsWith('error_')) {
          setDoc(doc(ref, entry.id), { status: 'failed' }, { merge: true }).catch(() => {});
          return { ...entry, status: 'failed' };
        }
        return entry;
      } catch (err) {
        console.warn('Could not check writing submission', entry.submissionId, err);
        return entry;
      }
    }));
  } catch (err) {
    console.error('loadPracticeWritingHistory error:', err);
    return [];
  }
}

async saveWritingSubmission(userId, submissionData) {
    if (!userId) {
      console.warn('saveWritingSubmission: no userId');
      return null;
    }
    if (!submissionData || !submissionData.texts || typeof submissionData.texts !== 'object' || Object.keys(submissionData.texts).length === 0) {
      console.warn('saveWritingSubmission: no submission data or empty texts', { hasData: !!submissionData, textKeys: submissionData?.texts ? Object.keys(submissionData.texts) : [] });
      return null;
    }

    try {
      const submissionRef = collection(db, 'writingSubmissions');
      const rawData = {
        userId,
        ...submissionData,
        status: 'submitted', // initial status
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      // Firestore does not allow undefined values; strip them so addDoc never fails
      const docData = Object.fromEntries(
        Object.entries(rawData).filter(([, v]) => v !== undefined)
      );
      const docRef = await addDoc(submissionRef, docData);
      console.log('Writing submission saved with ID:', docRef.id);
      return docRef.id; // Return the ID for listening
    } catch (error) {
      console.error('Error saving writing submission:', error);
      return null;
    }
  }
async saveExamResults(userId, examId, resultData) {
    if (!userId) return { success: false, error: 'User not authenticated' };

    try {
      const resultsRef = collection(db, 'users', userId, 'examResults');

      const completeResultData = {
        examId: examId,
        // Add all the result data
        ...resultData, // This will include scores, answers, flags, and time

        // Writing feedback (if available)
        writingFeedback: resultData.writingFeedback || null,
        writingData: resultData.writingData || null,
        writingSubmissionId: resultData.writingSubmissionId || null,

        // Speaking feedback (if available)
        speakingFeedback: resultData.speakingFeedback || null,

        // AI corrections and teacher feedback
        aiCorrections: resultData.aiCorrections || null,
        teacherCorrections: resultData.teacherCorrections || null,

        // Ensure we have overallScore
        overallScore: resultData.overallScore || resultData.totalScore || 0,

        // Timestamp
        completedAt: serverTimestamp()
      };

      const docRef = await addDoc(resultsRef, stripUndefined(completeResultData));
      console.log('Exam results saved with ID:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error saving exam results:', error);
      return { success: false, error: error.message };
    }
  }
  /**
   * The id of an identical result stored in the last 5 minutes, else null.
   *
   * Returns the id rather than a bare boolean so a caller that skips the save
   * can still hand back a usable examResult id (the writing-feedback sync needs
   * one). Fails safe: on any error it returns null and the save proceeds.
   */
  async findRecentDuplicateResult(userId, examId, timeSpent) {
    if (!userId || !examId) return null;

    try {
      // Window and match key both come from real duplicate data, not a guess.
      // One account had a single sitting written 11 times over 23 HOURS, so a
      // 5-minute window would have caught none of it. And the re-saves carried
      // DIFFERENT scores for the same sitting (the results page recomputes),
      // so matching on score suppressed the detection entirely.
      //
      // timeSpent is the reliable fingerprint: same exam finished in the exact
      // same number of seconds is the same sitting. Two genuine attempts
      // matching to the second essentially never happens — the real re-sits in
      // the data differ widely (3951 / 7962 / 4182 / 10908s).
      const windowStart = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const resultsRef = collection(db, 'users', userId, 'examResults');

      const q = query(
        resultsRef,
        where('examId', '==', examId),
        where('completedAt', '>=', windowStart),
        orderBy('completedAt', 'desc'),
        limit(25)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null; // No recent results found
      }

      // timeSpent 0 carries no signal (older rows and instant finishes all share
      // it), so never collapse on it.
      if (!timeSpent) return null;

      for (const doc of querySnapshot.docs) {
        const data = doc.data();
        if (data.timeSpent === timeSpent) {
          console.log(`Found a recent duplicate result (ID: ${doc.id}).`);
          return doc.id; // A duplicate was found
        }
      }

      return null; // No exact duplicates in the last 5 minutes
    } catch (error) {
      console.error('Error checking for duplicate results:', error);
      return null; // Fail safe, allow saving
    }
  }
  async getLatestExamResult(userId, examId) {
    if (!userId || !examId) return null;

    try {
      const resultsRef = collection(db, 'users', userId, 'examResults');
      const q = query(
        resultsRef,
        where('examId', '==', examId),
        orderBy('completedAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const latestResultDoc = querySnapshot.docs[0];
        const resultData = latestResultDoc.data();

        console.log("Found latest exam result:", latestResultDoc.id, {
          hasWritingFeedback: !!resultData.writingFeedback,
          hasSpeakingFeedback: !!resultData.speakingFeedback
        });

        // Check if we have writing feedback, if not try to fetch it
        if (resultData.writingSubmissionId && !resultData.writingFeedback) {
          try {
            const submissionDoc = await getDoc(doc(db, 'writingSubmissions', resultData.writingSubmissionId));
            if (submissionDoc.exists() && submissionDoc.data().status === 'corrected') {
              resultData.writingFeedback = submissionDoc.data();
            }
          } catch (error) {
            console.error('Error fetching writing feedback:', error);
          }
        }

        return {
          id: latestResultDoc.id,
          ...resultData,
          // Ensure we have overallScore for compatibility
          overallScore: resultData.totalScore || resultData.overallScore || 0
        };
      }

      console.log("No previous results found for exam:", examId);
      return null;
    } catch (error) {
      console.error('Error fetching latest exam result:', error);
      return null;
    }
  }

   async updateExamResultWithFeedback(userId, resultId, feedbackData) {
  if (!userId || !resultId) return { success: false, error: 'Missing required parameters' };

  try {
    const resultRef = doc(db, 'users', userId, 'examResults', resultId);

    const updateData = {
      updatedAt: serverTimestamp()
    };

    // Save feedback fields
    if (feedbackData.writingFeedback) {
      updateData.writingFeedback = feedbackData.writingFeedback;
      updateData.writingData = feedbackData.writingData;
    }

    if (feedbackData.speakingFeedback) {
      updateData.speakingFeedback = feedbackData.speakingFeedback;
    }

    if (feedbackData.aiCorrections) {
      updateData.aiCorrections = feedbackData.aiCorrections;
    }

    if (feedbackData.teacherCorrections) {
      updateData.teacherCorrections = feedbackData.teacherCorrections;
    }

    // ✅ NEW: Save score fields (for recalculated scores)
    if (feedbackData.overallScore !== undefined) {
      updateData.overallScore = feedbackData.overallScore;
    }

    if (feedbackData.maxScore !== undefined) {
      updateData.maxScore = feedbackData.maxScore;
    }

    if (feedbackData.percentage !== undefined) {
      updateData.percentage = feedbackData.percentage;
    }

    if (feedbackData.sectionScores !== undefined) {
      updateData.sectionScores = feedbackData.sectionScores;
    }

    if (feedbackData.writtenScore !== undefined) {
      updateData.writtenScore = feedbackData.writtenScore;
    }

    if (feedbackData.oralScore !== undefined) {
      updateData.oralScore = feedbackData.oralScore;
    }

    if (feedbackData.passed !== undefined) {
      updateData.passed = feedbackData.passed;
    }

    // telc C1 Hören Teil 3: the AI rater's points per note, kept with the
    // answers (userAnswers._notesPoints) so every later recalculation uses them.
    if (feedbackData.listeningNotesScores !== undefined) {
      updateData.listeningNotesScores = feedbackData.listeningNotesScores;
      updateData['userAnswers._notesPoints'] = feedbackData.userAnswers?._notesPoints || {};
    }

    console.log('📝 Updating exam result with:', updateData);
    await updateDoc(resultRef, updateData);
    console.log('Exam result updated with feedback');

    return { success: true };
  } catch (error) {
    console.error('Error updating exam result with feedback:', error);
    return { success: false, error: error.message };
  }
}
  async getExamResultsHistory(userId, examId) {
    if (!userId || !examId) {
      console.warn('User ID or Exam ID is missing.');
      return [];
    }

    try {
      const resultsRef = collection(db, 'users', userId, 'examResults');
      const q = query(
        resultsRef,
        where('examId', '==', examId),
        orderBy('completedAt', 'desc')
        // The 'limit(1)' is intentionally removed to fetch all results
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log("No previous results found for exam:", examId);
        return [];
      }

      // Map docs to include the document ID and data
      let results = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

      // Enrich with writing feedback from writingSubmissions when missing (e.g. old results saved before feedback was persisted)
      results = await Promise.all(results.map(async (result) => {
        if (result.writingSubmissionId && !result.writingFeedback) {
          try {
            const submissionDoc = await getDoc(doc(db, 'writingSubmissions', result.writingSubmissionId));
            if (submissionDoc.exists() && submissionDoc.data().status === 'corrected') {
              return { ...result, writingFeedback: submissionDoc.data() };
            }
          } catch (e) {
            console.warn('Could not fetch writing feedback for result', result.id, e);
          }
        }
        return result;
      }));

      return results;

    } catch (error) {
      console.error('Error fetching exam result history:', error);
      return []; // Return an empty array on error
    }
  }

  /**
   * Save a graded reading/listening practice attempt (per-question breakdown included)
   * so it can be browsed later in the practice History panel.
   */
  async savePracticeAttempt(userId, attemptData) {
    if (!userId || !attemptData) return { success: false, error: 'Missing userId or attemptData' };

    try {
      const attemptsRef = collection(db, 'users', userId, 'practiceAttempts');
      const docData = {
        ...attemptData,
        completedAt: serverTimestamp()
      };
      const docRef = await addDoc(attemptsRef, stripUndefined(docData));
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error saving practice attempt:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Fetch past reading/listening practice attempts for a given mode/provider/level.
   * @param {string} userId
   * @param {{mode: 'reading'|'listening', provider?: string, level?: string}} filters
   */
  async getPracticeAttemptsHistory(userId, { mode, provider, level } = {}) {
    if (!userId || !mode) return [];

    try {
      const attemptsRef = collection(db, 'users', userId, 'practiceAttempts');
      const constraints = [where('mode', '==', mode)];
      if (provider) constraints.push(where('provider', '==', provider));
      if (level) constraints.push(where('level', '==', level));

      const q = query(attemptsRef, ...constraints, orderBy('completedAt', 'desc'), limit(50));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      console.error('Error fetching practice attempts history:', error);
      return [];
    }
  }

  /**
   * Subscribe to exam result history in real time (e.g. when writingFeedback is synced to exam result).
   * @param {string} userId
   * @param {string} examId
   * @param {function(Array)} callback - called with array of { id, ...data } (enriched when writingFeedback missing)
   * @returns {function} unsubscribe
   */
  subscribeExamResultsHistory(userId, examId, callback) {
    if (!userId || !examId) {
      console.warn('subscribeExamResultsHistory: User ID or Exam ID is missing.');
      return () => {};
    }

    const resultsRef = collection(db, 'users', userId, 'examResults');
    const q = query(
      resultsRef,
      where('examId', '==', examId),
      orderBy('completedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        let results = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        results = await Promise.all(results.map(async (result) => {
          if (result.writingSubmissionId && !result.writingFeedback) {
            try {
              const submissionDoc = await getDoc(doc(db, 'writingSubmissions', result.writingSubmissionId));
              if (submissionDoc.exists() && submissionDoc.data().status === 'corrected') {
                return { ...result, writingFeedback: submissionDoc.data() };
              }
            } catch (e) {
              console.warn('Could not fetch writing feedback for result', result.id, e);
            }
          }
          return result;
        }));
        callback(results);
      },
      (error) => {
        console.error('subscribeExamResultsHistory error:', error);
        callback([]);
      }
    );

    return unsubscribe;
  }

  /**
   * Track study session - with error handling
   */
 async trackStudySession(sessionData) {
  const userId = auth.currentUser?.uid;
  if (!userId) return { success: false, error: 'User not authenticated' };

  try {
    console.log('Tracking study session:', sessionData.type, sessionData.title);

    // Convert duration to minutes
    const durationInMinutes = this.convertToMinutes(sessionData.duration, sessionData.durationUnit || 'seconds');

    const sessionRef = collection(db, 'users', userId, 'studySessions');

    // Firestore does not accept undefined; use null for optional fields
    const session = {
      type: sessionData.type ?? 'lesson',
      activityId: sessionData.activityId ?? null,
      title: sessionData.title ?? '',
      startTime: sessionData.startTime ?? serverTimestamp(),
      endTime: sessionData.endTime ?? serverTimestamp(),
      duration: durationInMinutes,
      score: sessionData.score ?? null,
      maxScore: sessionData.maxScore ?? null,
      percentage: sessionData.score != null && sessionData.maxScore != null
        ? Math.round((sessionData.score / sessionData.maxScore) * 100) : null,
      completed: sessionData.completed ?? false,
      correctAnswers: sessionData.correctAnswers ?? 0,
      totalAnswers: sessionData.totalAnswers ?? 0,
      mistakesCount: sessionData.mistakesCount ?? 0,
      hintsUsed: sessionData.hintsUsed ?? 0,
      timePerQuestion: Array.isArray(sessionData.timePerQuestion) ? sessionData.timePerQuestion : [],
      skillsWorked: Array.isArray(sessionData.skillsWorked) ? sessionData.skillsWorked : [],
      topicsStudied: Array.isArray(sessionData.topicsStudied) ? sessionData.topicsStudied : [],
      difficultyLevel: sessionData.difficultyLevel ?? 'beginner',
      vocabularyLearned: Array.isArray(sessionData.vocabularyLearned) ? sessionData.vocabularyLearned : [],
      vocabularyReviewed: Array.isArray(sessionData.vocabularyReviewed) ? sessionData.vocabularyReviewed : [],
      examProvider: sessionData.examProvider ?? null,
      examLevel: sessionData.examLevel ?? null,
      sectionScores: sessionData.sectionScores && typeof sessionData.sectionScores === 'object' ? sessionData.sectionScores : {},
      deviceType: navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop',
      location: sessionData.location ?? null,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(sessionRef, session);

   await this.updateUserStats(userId, { ...session, duration: durationInMinutes });
  await this.updateStudyStreak(userId);

  return { success: true, id: docRef.id, data: session };
  } catch (error) {
    console.error('Error tracking study session:', error);
    return { success: false, error: error.message };
  }
}

// Move this INSIDE the class
convertToMinutes(duration, unit) {
  switch(unit) {
    case 'milliseconds': return Math.round(duration / 1000 / 60);
    case 'seconds': return Math.round(duration / 60);
    case 'minutes': return duration;
    default: return Math.round(duration / 60); // assume seconds
  }
}
async getUserProgress(userId) {
  if (!userId) return this.getDefaultProgress(null);

  try {
    const profile = await this.getUserProfile(userId);
    return profile || this.getDefaultProgress(userId);
  } catch (error) {
    console.error('Error in getUserProgress:', error);
    return this.getDefaultProgress(userId);
  }
}

// Add default progress structure for compatibility
getDefaultProgress(userId) {
  return {
    userId: userId,
    totalAnswered: 0,
    correctAnswers: 0,
    sectionScores: {},
    completedWeeks: [],
    studyStreak: 0,
    lastStudyDate: null,
    overallProgress: 0,
    sectionProgress: {},
    stats: {
      currentStreak: 0,
      longestStreak: 0,
      totalStudyTimeMinutes: 0,
      lessonsCompleted: 0,
      examsCompleted: 0,
      vocabularyWordsLearned: 0,
      grammarTopicsCompleted: 0,
      overallAccuracy: 0
    }
  };
}
async updateUserStats(userId, sessionData) {
  if (!userId) return;

  try {
    const userRef = doc(db, 'users', userId);

    // Simple stats update without streak logic
    const updates = {
      totalAnswered: increment(sessionData.totalAnswers || 0),
      correctAnswers: increment(sessionData.correctAnswers || 0),
      'stats.totalStudyTimeMinutes': increment(sessionData.duration || 0),
      'stats.totalQuestionsAttempted': increment(sessionData.totalAnswers || 0),
      'stats.totalCorrectAnswers': increment(sessionData.correctAnswers || 0),
      updatedAt: serverTimestamp()
    };

    // Update based on activity type
    switch(sessionData.type) {
      case 'lesson':
        updates['stats.lessonsCompleted'] = increment(1);
        break;
      case 'exam':
        updates['stats.examsCompleted'] = increment(1);
        break;
      case 'vocabulary':
        updates['stats.vocabularyWordsReviewed'] = increment(sessionData.vocabularyLearned?.length || 0);
        break;
      case 'grammar':
        updates['stats.grammarExercisesCompleted'] = increment(1);
        break;
      case 'practice':
        updates['stats.practiceSessionsCompleted'] = increment(1);
        break;
    }

    await updateDoc(userRef, updates);
    console.log('User stats updated successfully');

  } catch (error) {
    console.error('Error updating user stats:', error);
  }
}

/**
 * Save study plan to Firebase
 * This persists the user's personalized study plan to Firestore
 */
async saveStudyPlan(userId, planData) {
  if (!userId || !planData) {
    console.error('Cannot save study plan: missing userId or planData');
    return { success: false, error: 'Missing required parameters' };
  }

  try {
    const userRef = doc(db, 'users', userId);
    
    // Structure the study plan data for Firebase
    const studyPlanUpdate = {
      studyPlan: {
        currentPlan: planData,
        planStartDate: serverTimestamp(),
        planEndDate: planData.examDate || null,
        intensity: planData.intensity || 'moderate',
        focusAreas: planData.weakSkills || planData.focusAreas || [],
        planProgress: planData.planProgress || 0,
        // Store the wizard form data for reference
        formData: {
          currentLevel: planData.currentLevel || null,
          targetLevel: planData.targetLevel || null,
          targetExam: planData.targetExam || null,
          immersion: planData.immersion || null,
          weeklyHours: planData.weeklyHours || null,
          months: planData.months || null,
          studyDays: planData.studyDays || null,
          weakSkills: planData.weakSkills || [],
          grammarFocus: planData.grammarFocus || null,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    };

    await updateDoc(userRef, studyPlanUpdate);
    console.log('Study plan saved to Firebase successfully');
    return { success: true };
  } catch (error) {
    console.error('Error saving study plan to Firebase:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get study plan from Firebase
 * Retrieves the user's personalized study plan from Firestore
 */
async getStudyPlan(userId) {
  if (!userId) {
    console.warn('Cannot get study plan: no userId provided');
    return null;
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const studyPlan = userData.studyPlan;
      
      // Check if user has a valid study plan
      if (studyPlan && studyPlan.currentPlan) {
        console.log('Study plan loaded from Firebase');
        return studyPlan.currentPlan;
      }
    }
    
    console.log('No study plan found in Firebase');
    return null;
  } catch (error) {
    console.error('Error getting study plan from Firebase:', error);
    return null;
  }
}

/**
 * Update study plan intensity/settings
 */
async updateStudyPlanSettings(userId, updates) {
  if (!userId) {
    return { success: false, error: 'No userId provided' };
  }

  try {
    const userRef = doc(db, 'users', userId);
    
    const updateData = {
      'studyPlan.updatedAt': serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Map the updates to the correct paths
    if (updates.intensity) {
      updateData['studyPlan.intensity'] = updates.intensity;
      updateData['studyPlan.currentPlan.intensity'] = updates.intensity;
    }
    if (updates.planProgress !== undefined) {
      updateData['studyPlan.planProgress'] = updates.planProgress;
    }

    await updateDoc(userRef, updateData);
    console.log('Study plan settings updated in Firebase');
    return { success: true };
  } catch (error) {
    console.error('Error updating study plan settings:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Clear/reset study plan
 */
async clearStudyPlan(userId) {
  if (!userId) {
    return { success: false, error: 'No userId provided' };
  }

  try {
    const userRef = doc(db, 'users', userId);
    
    await updateDoc(userRef, {
      studyPlan: {
        currentPlan: null,
        planStartDate: null,
        planEndDate: null,
        intensity: 'moderate',
        focusAreas: [],
        planProgress: 0
      },
      updatedAt: serverTimestamp()
    });

    console.log('Study plan cleared in Firebase');
    return { success: true };
  } catch (error) {
    console.error('Error clearing study plan:', error);
    return { success: false, error: error.message };
  }
  }

  /**
   * Save exam selection to Firebase
   * @param {string} userId - User ID
   * @param {Object} examData - Exam data object with examProvider and level
   * @returns {Promise<Object>} Save result
   */
  async saveExamSelection(userId, examData) {
    if (!userId || !examData) {
      console.error('Cannot save exam selection: missing userId or examData');
      return { success: false, error: 'Missing required parameters' };
    }

    try {
      const userRef = doc(db, 'users', userId);
      
      const provider = examData.examProvider ?? examData.provider ?? null;
      const level = examData.level ?? examData.examLevel ?? null;
      const examUpdate = {
        selectedExamProvider: provider,
        selectedExamLevel: level,
        examSelection: {
          examProvider: provider,
          level,
          examName: examData.examName ?? null,
          examId: examData.examId ?? null,
          updatedAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      };

      await updateDoc(userRef, examUpdate);
      console.log('Exam selection saved to Firebase successfully');
      return { success: true };
    } catch (error) {
      console.error('Error saving exam selection to Firebase:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get exam selection from Firebase
   * @param {string} userId - User ID
   * @returns {Promise<Object|null>} Exam data or null
   */
  async getExamSelection(userId) {
    if (!userId) {
      console.warn('Cannot get exam selection: no userId provided');
      return null;
    }

    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check if user has exam selection saved
        if (userData.examSelection && userData.examSelection.examProvider && userData.examSelection.level) {
          console.log('Exam selection loaded from Firebase');
          return {
            examProvider: userData.examSelection.examProvider,
            level: userData.examSelection.level,
            examName: userData.examSelection.examName || null,
            examId: userData.examSelection.examId || null
          };
        }
        
        // Fallback to legacy fields
        if (userData.selectedExamProvider && userData.selectedExamLevel) {
          console.log('Exam selection loaded from Firebase (legacy fields)');
          return {
            examProvider: userData.selectedExamProvider,
            level: userData.selectedExamLevel,
            examName: null,
            examId: null
          };
        }
      }
      
      console.log('No exam selection found in Firebase');
      return null;
    } catch (error) {
      console.error('Error getting exam selection from Firebase:', error);
      return null;
    }
  }

  /**
   * Save onboarding completion and payload (for Personalized Plan header).
   * @param {string} userId
   * @param {Object} payload - { examProvider, level, examName, testDate, weakestSubject, focusAreas, placementLevel, ... }
   */
  async saveOnboardingComplete(userId, payload) {
    if (!userId || !payload) {
      console.error('saveOnboardingComplete: missing userId or payload');
      return { success: false, error: 'Missing parameters' };
    }
    try {
      const userRef = doc(db, 'users', userId);
      const normalizedHeardFrom = Array.isArray(payload.heardFrom)
        ? payload.heardFrom.map((item) => String(item).trim()).filter(Boolean)
        : String(payload.heardFrom || '').split(',').map((item) => item.trim()).filter(Boolean);

      const examUpdate = {
        selectedExamProvider: payload.examProvider || null,
        selectedExamLevel: payload.level || null,
        examSelection: {
          examProvider: payload.examProvider || null,
          level: payload.level || null,
          examName: payload.examName || null,
          updatedAt: serverTimestamp()
        },
        onboardingComplete: true,
        onboardingData: {
          examName: payload.examName || null,
          testDate: payload.testDate || null,
          weakestSubject: payload.weakestSubject || null,
          focusAreas: payload.focusAreas || [],
          heardFrom: normalizedHeardFrom,
          purpose: payload.purpose || null,
          placementLevel: payload.placementLevel || null,
          placementScore: payload.placementScore ?? null,
          placementAnswers: Array.isArray(payload.placementAnswers) ? payload.placementAnswers : [],
          pricingPlanId: payload.pricingPlanId || null,
          completedAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      };
      await updateDoc(userRef, stripUndefined(examUpdate));
      console.log('Onboarding saved to Firebase');
      return { success: true };
    } catch (error) {
      console.error('Error saving onboarding:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get onboarding completion and data from profile.
   */
  async getOnboardingComplete(userId) {
    if (!userId) return null;
    try {
      const profile = await this.getUserProfile(userId);
      if (!profile?.onboardingComplete) return null;
      return profile.onboardingData || {};
    } catch (error) {
      console.error('Error getting onboarding:', error);
      return null;
    }
  }

  /**
   * Update user voice preferences for TTS
   */
  async updateVoicePreferences(userId, voicePrefs) {
    if (!userId) throw new Error('User ID required');
    
    try {
      console.log('Updating voice preferences for user:', userId);
      await updateDoc(doc(db, 'users', userId), {
        voicePreferences: {
          vocabulary: voicePrefs.vocabulary || 'female_clear',
          redemittel: voicePrefs.redemittel || 'female_warm',
          updatedAt: serverTimestamp()
        }
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating voice preferences:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user voice preferences (with defaults)
   */
  async getVoicePreferences(userId) {
    if (!userId) return null;
    
    try {
      const profile = await this.getUserProfile(userId);
      return profile?.voicePreferences || {
        vocabulary: 'female_clear',
        redemittel: 'female_warm'
      };
    } catch (error) {
      console.error('Error getting voice preferences:', error);
      return {
        vocabulary: 'female_clear',
        redemittel: 'female_warm'
      };
    }
  }
}

export default new EnhancedUserService();